import { buildStepUpForm, type StepUpFormHandle, type StepUpFormOptions, type StepUpResult } from './form'
import { StepUpError } from './errors'
import { stepUpFormStyles, stepUpModalStyles } from './style'
import { translate } from '../i18n'

// Only one step-up modal may be on screen at a time. The entry lives until the dialog is
// actually gone from the DOM, which for a terminal error is later than the Promise settling
// (the form stays up with an explanatory message and its own Close). `settled` separates the
// two cases: a still-live modal makes a second call fail, while one that is only showing its
// leftover message is replaced — otherwise an app that catches a terminal error and retries
// would be refused forever, with no new dialog and no visible reason.
let activeModal: { settled: boolean; dismiss: () => void } | undefined

export function openStepUpModal(options: StepUpFormOptions): Promise<StepUpResult> {
  if (activeModal) {
    if (!activeModal.settled) {
      return Promise.reject(new StepUpError('already_open', 'A step-up modal is already open.'))
    }
    activeModal.dismiss()
  }

  return new Promise((resolve, reject) => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null

    const host = document.createElement('div')
    document.body.appendChild(host)

    const shadow = host.attachShadow({ mode: 'open' })
    const style = document.createElement('style')
    style.textContent = stepUpFormStyles + stepUpModalStyles
    shadow.appendChild(style)

    const backdrop = document.createElement('div')
    backdrop.className = 'zid-stepup-backdrop'

    const dialog = document.createElement('div')
    dialog.className = 'zid-stepup-dialog'
    dialog.setAttribute('role', 'dialog')
    dialog.setAttribute('aria-modal', 'true')

    const closeButton = document.createElement('button')
    closeButton.type = 'button'
    closeButton.className = 'zid-stepup-close'
    closeButton.setAttribute('aria-label', translate('StepUp.Close', options.locale))
    closeButton.textContent = '×'

    const formContainer = document.createElement('div')

    dialog.append(closeButton, formContainer)
    backdrop.appendChild(dialog)
    shadow.appendChild(backdrop)

    const entry = { settled: false, dismiss: cleanup }
    activeModal = entry

    function cleanup(): void {
      if (activeModal === entry) {
        activeModal = undefined
      }
      document.removeEventListener('keydown', onKeydown)
      host.remove()
      previouslyFocused?.focus()
    }

    // Keeps keyboard focus inside the dialog while it's open, matching aria-modal="true" — the
    // set of focusable elements is re-queried on every Tab because the form swaps its own
    // content (discovery message → form → terminal error) while the dialog stays mounted.
    function trapFocus(event: KeyboardEvent): void {
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      )
      if (focusable.length === 0) {
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = shadow.activeElement
      if (event.shiftKey) {
        if (active === first || !focusable.includes(active as HTMLElement)) {
          event.preventDefault()
          last.focus()
        }
      } else if (active === last || !focusable.includes(active as HTMLElement)) {
        event.preventDefault()
        first.focus()
      }
    }

    // Every close gesture is routed through the form rather than rejecting here: the form is the
    // one that knows whether an outcome is already on its way, and it aborts an in-flight
    // verification before cancelling instead of letting its result land on a dead Promise.
    // Once it has settled, the dialog is only still up to explain a terminal error, so closing
    // it is a plain teardown — there's nothing left to cancel.
    function requestClose(): void {
      if (entry.settled) {
        cleanup()
        return
      }
      handle.requestCancel()
    }

    function onKeydown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        requestClose()
        return
      }
      if (event.key === 'Tab') {
        trapFocus(event)
      }
    }
    document.addEventListener('keydown', onKeydown)

    closeButton.addEventListener('click', requestClose)
    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) {
        requestClose()
      }
    })

    const handle: StepUpFormHandle = buildStepUpForm(
      formContainer,
      options,
      (result) => {
        entry.settled = true
        resolve(result)
      },
      (error) => {
        entry.settled = true
        reject(error)
      },
      cleanup,
    )
  })
}
